import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  role: 'admin' | 'user';
  company_id: string | null;
  created_at: string;
  updated_at: string;
}

interface UserWithProfile {
  id: string;
  email: string;
  role: 'admin' | 'user';
  full_name: string | null;
  company_id: string | null;
  needsOnboarding: boolean;
}

interface AuthContextType {
  user: UserWithProfile | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, fullName?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserWithProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Configuration de l'écoute des changements d'authentification
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        if (session?.user) {
          // Récupérer le profil utilisateur
          setTimeout(async () => {
            try {
              const { data: profile, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('user_id', session.user.id)
                .single();

              if (error) {
                console.error('Erreur lors de la récupération du profil:', error);
                setUser(null);
              } else if (profile) {
                setUser({
                  id: session.user.id,
                  email: profile.email,
                  role: profile.role as 'admin' | 'user',
                  full_name: profile.full_name,
                  company_id: profile.company_id,
                  needsOnboarding: !profile.company_id || !profile.full_name
                });

                // Try to accept invitation if we have a token captured
                setTimeout(async () => {
                  let inviteToken: string | null = null;
                  let inviteEmail: string | null = null;
                  
                  try {
                    // Get tokens from localStorage (cannot use hooks in callbacks)
                    inviteToken = localStorage.getItem('inviteToken');
                    inviteEmail = localStorage.getItem('inviteEmail');
                    
                    if (inviteToken) {
                      // Processing invitation acceptance
                      const { data: acceptData, error: acceptError } = await supabase.rpc('accept_team_invitation', {
                        p_invitation_token: inviteToken,
                      });

                      if (acceptError) {
                        // Invitation acceptance failed
                        console.warn('Invitation acceptance failed:', acceptError);
                      } else if (session.user?.id) {
                        // Invitation accepted successfully
                        // Refresh profile after acceptance to update company_id
                        const { data: refreshedProfile, error: refreshedError } = await supabase
                          .from('profiles')
                          .select('*')
                          .eq('user_id', session.user.id)
                          .single();
                        
                        if (!refreshedError && refreshedProfile) {
                          setUser({
                            id: session.user.id,
                            email: refreshedProfile.email,
                            role: refreshedProfile.role as 'admin' | 'user',
                            full_name: refreshedProfile.full_name,
                            company_id: refreshedProfile.company_id,
                            needsOnboarding: !refreshedProfile.company_id || !refreshedProfile.full_name
                          });
                        }
                      }

                      // Clear tokens to avoid reprocessing
                      localStorage.removeItem('inviteToken');
                      localStorage.removeItem('inviteEmail');
                    }
                  } catch (err) {
                    console.error('Error processing invitation:', err);
                  }
                }, 100);
              }
            } catch (err) {
              console.error('Erreur lors de la récupération du profil:', err);
              setUser(null);
            }
          }, 0);
        } else {
          setUser(null);
        }
        
        setIsLoading(false);
      }
    );

    // Vérifier s'il y a une session existante
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Log failed login attempt directly without hooks
        setTimeout(async () => {
          try {
            await supabase.rpc('log_security_event', {
              p_event_type: 'login_failed',
              p_event_data: {
                email: email.toLowerCase(),
                reason: error.message,
                timestamp: new Date().toISOString()
              }
            });
          } catch (err) {
            console.error('Failed to log security event:', err);
          }
        }, 0);
        
        return { success: false, error: error.message };
      }

      // Log successful login
      if (data.user) {
        setTimeout(async () => {
          try {
            await supabase.rpc('log_security_event', {
              p_event_type: 'login_success',
              p_event_data: {
                user_id: data.user.id,
                timestamp: new Date().toISOString()
              }
            });
          } catch (err) {
            console.error('Failed to log security event:', err);
          }
        }, 0);
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Erreur de connexion' };
    }
  };

  const register = async (email: string, password: string, fullName?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName || ''
          }
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Erreur lors de l\'inscription' };
    }
  };

  const logout = async (): Promise<void> => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
